/* eslint-disable @reatom/unit-naming-rule */
import { action, onConnect } from '@reatom/framework'

import { AsyncStateN, pipe, Result } from '@blade/std-lib'
import { currencyRegistry } from '@root/entities/currency/atoms'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { pairRegistry } from '@root/entities/pair/atoms'
import {
  premiumMetaRegistry,
  premiumMetaUpdateReceived,
} from '@root/entities/premium'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { ApiError, apiRequest } from '@root/shared/api'
import { reatomRequest } from '@root/shared/lib'

import type { CurrencyAddress } from '@root/entities/currency/model/CurrencyAddress'

export const fetchPremiumCurrencyInfoRequestAction = action(
  async (
    ctx,
    {
      currencyAddress,
      network,
    }: { network: Network.Network; currencyAddress: CurrencyAddress },
  ) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.premium.premiumTokenInfoList, [
        {
          network: Network.toDto(network),
          tokenAddress: currencyAddress,
        },
      ]),
    )

    const mappedResult = pipe(
      result,
      Result.mapError(ApiError.fallbackToUnknown),
      Result.map((it) => ({
        currency: it.tokenMeta,
        premiumInfo: it.premiumInfo,
        pair: it.pairMeta,
      })),
    )

    if (Result.isSuccess(mappedResult)) {
      premiumMetaUpdateReceived(ctx, {
        currency: mappedResult.success.currency,
        premiumMeta: mappedResult.success.premiumInfo,
      })
    }

    return mappedResult
  },
  'fetchPremiumCurrencyInfo',
)

export const reatomPremiumCurrencyInfo = ({
  currencyAddress,
}: {
  currencyAddress: CurrencyAddress
}) => {
  // To keep ui updated, after token updates

  const fetchInfoAction = action(async (ctx) => {
    const network = ctx.get(selectedNetworkAtom)

    const result = await ctx.schedule(() =>
      fetchPremiumCurrencyInfoRequestAction(ctx, {
        currencyAddress,
        network,
      }),
    )

    if (Result.isSuccess(result)) {
      // currencyUpdateReceivedEvent(ctx, result.success.currency)
      premiumMetaUpdateReceived(ctx, {
        currency: result.success.currency,
        premiumMeta: result.success.premiumInfo,
      })
    }

    return result.pipe(
      Result.map((it) => ({
        premiumMeta: premiumMetaRegistry.resolve(ctx, {
          dto: it.premiumInfo,
          currency: currencyRegistry.resolveFromTokenMeta(ctx, it.currency),
        }),
        pair: it.pair ? pairRegistry.resolveFromDto(ctx, it.pair) : undefined,
      })),
    )
  }, 'fetchInfoAction')

  const stateAtom = reatomRequest({
    execAction: fetchInfoAction,
    name: `${currencyAddress}_premiumAtom`,
  })

  onConnect(stateAtom, (ctx) => {
    if (AsyncStateN.isSuccess(ctx.get(stateAtom))) {
      return
    }
    stateAtom.execute(ctx)
  })

  return {
    pairState: stateAtom,
    loadPairAction: stateAtom.execute,
  }
}
export type PremiumCurrencyInfoModel = ReturnType<
  typeof reatomPremiumCurrencyInfo
>
